import { useEffect, useMemo, useState } from "react";
import { MapPin, ChevronLeft, Loader2 } from "lucide-react";
import { stateName } from "@/lib/geo";
import api from "@/lib/api";
import type { LocationOption } from "@/lib/locations";

const CITIES_PAGE = 24;

interface Props {
  state?: string;
  city?: string;
  onSelect: (next: { state: string; city: string }) => void;
}

export default function ExploreLocations({ state, city, onSelect }: Props): React.JSX.Element {
  const [states, setStates] = useState<LocationOption[] | null>(null);
  const [cities, setCities] = useState<LocationOption[] | null>(null);
  const [citiesAll, setCitiesAll] = useState<LocationOption[]>([]);
  const [citiesPage, setCitiesPage] = useState(CITIES_PAGE);

  useEffect(() => {
    if (!state) {
      setCities(null);
      setCitiesAll([]);
      setCitiesPage(CITIES_PAGE);
      return;
    }
    let cancelled = false;
    api
      .get("/locations/options", { params: { state } })
      .then((r) => {
        if (cancelled) return;
        setCitiesAll(r.data?.cities || []);
        setCities(r.data?.cities || []);
        setCitiesPage(CITIES_PAGE);
      })
      .catch(() => {
        if (cancelled) return;
        setCitiesAll([]);
        setCities([]);
        setCitiesPage(CITIES_PAGE);
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  useEffect(() => {
    if (states !== null) return;
    api
      .get("/locations/options")
      .then((r) => setStates(r.data?.states || []))
      .catch(() => setStates([]));
  }, []);

  const showCities = !!state;
  const sortedCities = useMemo(
    () =>
      [...citiesAll].sort((a, b) => {
        if ((b.count ?? 0) !== (a.count ?? 0)) return (b.count ?? 0) - (a.count ?? 0);
        return a.label.localeCompare(b.label);
      }),
    [citiesAll],
  );
  const citiesVisible = showCities
    ? sortedCities.slice(0, citiesPage)
    : [];
  const hasMore = sortedCities.length > citiesPage;

  const handleState = (uf: string) => {
    const same = state?.toUpperCase() === uf.toUpperCase();
    onSelect(same ? { state: "", city: "" } : { state: uf, city: "" });
  };

  const handleCity = (cityVal: string) => {
    onSelect({ state: state || "", city: cityVal });
  };

  const empty = states !== null && states.length === 0;

  return (
    <section
      className="pt-8 pb-16 lg:pt-12 lg:pb-24 bg-cream-50"
      data-testid="explore-locations"
    >
      <div className="container-custom">
        <div className="mb-10">
          <span className="overline mb-3 block">Explorar</span>
          <h2 className="h-section text-3xl sm:text-4xl text-ink-900">
            Encontre acompanhantes{" "}
            <em className="text-heat-grad not-italic">por região</em>
          </h2>
          <p className="text-ink-600 text-sm mt-2 max-w-xl">
            Selecione um estado para ver acompanhantes da sua região. Clique
            depois em uma cidade para aprofundar a busca.
          </p>
        </div>

        {empty ? (
          <div className="text-center py-16 rounded-3xl border-2 border-dashed border-cream-300 bg-white">
            <MapPin className="w-10 h-10 mx-auto mb-3 text-ink-300" />
            <p className="text-ink-500">
              Nenhum acompanhante registrado por enquanto.
            </p>
            <p className="text-ink-400 text-sm mt-1">
              Volte em breve ou compartilhe a plataforma.
            </p>
          </div>
        ) : null}

        {showCities && (
          <div className="flex items-center gap-2 mb-6">
            <button
              type="button"
              onClick={() => onSelect({ state: "", city: "" })}
              className="inline-flex items-center gap-2 text-heat-700 hover:text-heat-800 font-semibold text-sm underline decoration-heat-300/50 underline-offset-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Todos os estados
            </button>
          </div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {(states || []).map((opt) => {
            const uf = opt.value?.toUpperCase();
            if (!uf) return null;
            const stateUpper = state ? state.toUpperCase() : "";
            const active = stateUpper === uf;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleState(uf)}
                aria-pressed={active}
                className={`group relative bg-white border rounded-2xl p-4 sm:p-5 text-center transition-all duration-200 cursor-pointer ${
                  active
                    ? "border-heat-500 shadow-heat-glow -translate-y-1"
                    : "border-cream-200 hover:border-heat-300 hover:-translate-y-1 hover:shadow-heat-glow"
                }`}
              >
                <div
                  className={`w-11 h-11 mx-auto mb-3 rounded-2xl flex items-center justify-center text-heat-700 transition-all ${
                    active
                      ? "bg-heat-grad text-white"
                      : "bg-heat-grad-soft group-hover:bg-heat-grad group-hover:text-white"
                  }`}
                >
                  <MapPin className="w-5 h-5" />
                </div>
                <span className="block font-display font-black text-lg text-ink-900">
                  {uf}
                </span>
                <p
                  className={`mt-1 text-xs ${
                    active ? "text-heat-800" : "text-ink-500"
                  } truncate`}
                  title={stateName(uf)}
                >
                  {stateName(uf) || uf}
                </p>
                {typeof opt.count === "number" && opt.count > 0 && (
                  <span
                    className={`mt-1 block text-[10px] font-bold uppercase tracking-widest ${
                      active ? "text-heat-700" : "text-ink-400"
                    }`}
                  >
                    {opt.count} {opt.count === 1 ? "perfil" : "acompanhantes"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {states === null && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="h-[96px] rounded-2xl bg-cream-100 animate-pulse"
              />
            ))}
          </div>
        )}

        {showCities && (
          <div className="mt-14">
            <div className="mb-6 flex items-baseline justify-between">
               <h3 className="text-xl font-display font-semibold text-ink-900">
                 Cidades em {state ? stateName(state) : state}
               </h3>
              {citiesAll.length > 0 && (
                <span className="text-xs text-ink-400">
                  {citiesAll.length} cidade{citiesAll.length === 1 ? "s" : "s"}
                </span>
              )}
            </div>

            {cities === null ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-20 rounded-xl bg-cream-100 animate-pulse"
                  />
                ))}
              </div>
            ) : cities.length === 0 ? (
              <div className="text-center py-12 rounded-3xl border-2 border-dashed border-cream-300 bg-white">
                <p className="text-ink-500">
                  Nenhuma cidade com acompanhantes neste estado.
                </p>
                <button
                  type="button"
                  onClick={() => onSelect({ state: "", city: "" })}
                  className="mt-4 text-sm font-semibold text-heat-700 hover:text-heat-800"
                >
                  Ver todos os estados
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {citiesVisible.map((opt) => {
                  const cityLower = city ? city.toLowerCase() : "";
                  const activeCity = cityLower === opt.label.toLowerCase();
                  return (
                    <button
                      key={opt.value || opt.label}
                      type="button"
                      onClick={() => handleCity(opt.label)}
                      aria-pressed={activeCity}
                      className={`group bg-white border rounded-xl p-4 text-center transition-all duration-200 cursor-pointer ${
                        activeCity
                          ? "border-heat-500 shadow-heat-glow -translate-y-1"
                          : "border-cream-200 hover:border-heat-300 hover:-translate-y-1 hover:shadow-heat-glow"
                      }`}
                    >
                      <div className="w-9 h-9 mx-auto mb-2 rounded-xl bg-heat-grad-soft flex items-center justify-center text-heat-700 group-hover:bg-heat-grad group-hover:text-white transition-all">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <p
                        className={`text-sm font-semibold truncate ${
                          activeCity ? "text-heat-800" : "text-ink-800"
                        }`}
                        title={opt.label}
                      >
                        {opt.label}
                      </p>
                      {typeof opt.count === "number" && opt.count > 0 && (
                        <span className="mt-0.5 block text-[10px] text-ink-400">
                          {opt.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {hasMore && (
              <button
                type="button"
                onClick={() => setCitiesPage((p) => p + CITIES_PAGE)}
                className="mt-6 mx-auto flex items-center gap-2 text-sm font-bold text-heat-700 hover:text-heat-800"
              >
                <Loader2 className="w-3 h-3" />
                Mostrar mais
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}